import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { SPECIAL_INSTRUCTION_WIDTH } from '@erp/rpc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { toast } from 'src/components/ui/toast';
import {
  useSetDraftSubmission,
  type DraftPurchaseOrderSubmission,
} from 'src/api/rpc/hooks/procurement/usePurchaseOrderSubmission';
import type { SubmissionDefaults } from '@erp/rpc';
import {
  creditTermsLabel,
  formFromSubmission,
  NO_CODE,
  submissionSaveInput,
  vendorEmailToOffer,
  withSendByEdi,
  type DraftSubmissionForm,
} from './draftSubmissionForm';

const USAGE_CODES = ['P', 'B'];
/** "No credit terms" in the picker, saved as empty. */
const NO_CREDIT_TERMS = 'none';

/** Edits how a draft PO is sent: EDI (off only), email fields, terms, lines. */
export function DraftSubmissionEditor({
  submission,
  onDone,
}: {
  submission: DraftPurchaseOrderSubmission;
  onDone: () => void;
}) {
  const gt = useGT();
  const setSubmission = useSetDraftSubmission();
  // Locked while saving: the save sends the form as it was when clicked, and
  // closing the editor would not stop the write.
  const saving = setSubmission.isPending;
  const [form, setForm] = useState(() => formFromSubmission(submission));

  function save() {
    setSubmission.mutate(submissionSaveInput(submission, form), {
      onSuccess: () => {
        toast.success(
          gt('Sending details updated.', {
            context: 'toast after changing how a draft PO is sent',
          }),
        );
        onDone();
      },
      onError: (error) => toast.error(error.message),
    });
  }

  return (
    <div className="grid gap-2">
      <fieldset disabled={saving} className="grid min-w-0 gap-2">
        <DraftSubmissionFields
          idPrefix="draft-submission"
          form={form}
          setup={submission}
          disabled={saving}
          onChange={setForm}
        />
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saving}
          onClick={onDone}
        >
          <T context="dialog button">Cancel</T>
        </Button>
        <Button type="button" size="sm" disabled={saving} onClick={save}>
          <T context="dialog button">Save</T>
        </Button>
      </div>
    </div>
  );
}

/**
 * The sending fields: EDI (off only), email fields, credit terms and printed
 * lines, for a draft's Sending panel and the release dialog.
 */
export function DraftSubmissionFields({
  idPrefix,
  form,
  setup,
  disabled = false,
  onChange,
}: {
  idPrefix: string;
  form: DraftSubmissionForm;
  setup: Pick<
    SubmissionDefaults,
    'vendorEdi' | 'vendorEmail' | 'creditTermsCodes'
  >;
  disabled?: boolean;
  onChange: (form: DraftSubmissionForm) => void;
}) {
  const field =
    (key: 'emailTo' | 'emailCc' | 'emailSubject') =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...form, [key]: event.target.value });
  return (
    <>
      <EdiField
        id={`${idPrefix}-edi`}
        form={form}
        vendorEdi={setup.vendorEdi}
        disabled={disabled}
        onChange={(sendByEdi) =>
          onChange(withSendByEdi(form, sendByEdi, setup.vendorEmail))
        }
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <TextField
          id={`${idPrefix}-to`}
          value={form.emailTo}
          type="email"
          onChange={field('emailTo')}
        >
          <T context="draft PO sending field: email recipient">To</T>
        </TextField>
        <TextField
          id={`${idPrefix}-cc`}
          value={form.emailCc}
          type="email"
          onChange={field('emailCc')}
        >
          <T context="draft PO sending field: email copy">Cc</T>
        </TextField>
      </div>
      <VendorEmailOffer
        email={vendorEmailToOffer(form, setup.vendorEmail)}
        onUse={(emailTo) => onChange({ ...form, emailTo })}
      />
      <TextField
        id={`${idPrefix}-subject`}
        value={form.emailSubject}
        onChange={field('emailSubject')}
      >
        <T context="draft PO sending field: email subject line">Subject</T>
      </TextField>
      <CreditTermsField
        id={`${idPrefix}-credit-terms`}
        value={form.creditTermsCode}
        codes={setup.creditTermsCodes}
        disabled={disabled}
        onChange={(creditTermsCode) => onChange({ ...form, creditTermsCode })}
      />
      <InstructionRows
        rows={form.instructions}
        disabled={disabled}
        onChange={(instructions) => onChange({ ...form, instructions })}
      />
    </>
  );
}

function EdiField({
  id,
  form,
  vendorEdi,
  disabled,
  onChange,
}: {
  id: string;
  form: DraftSubmissionForm;
  vendorEdi: boolean | null;
  disabled: boolean;
  onChange: (sendByEdi: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={form.sendByEdi}
        // EDI can always be switched off, never on for a vendor without it.
        disabled={disabled || (vendorEdi !== true && !form.sendByEdi)}
        onCheckedChange={(checked) => onChange(checked === true)}
      />
      <Label htmlFor={id}>
        <T context="draft PO sending: checkbox to send the PO by EDI">
          Send by EDI
        </T>
      </Label>
      {vendorEdi !== true ? (
        <span className="text-muted-foreground">
          <T context="draft PO sending: why the EDI checkbox is disabled">
            This vendor is not set up for EDI.
          </T>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Sent by email but not to the vendor's PO address (an EDI copy left on To
 * after switching EDI off, or a typed address): say so and offer the vendor's.
 */
function VendorEmailOffer({
  email,
  onUse,
}: {
  email: string | null;
  onUse: (email: string) => void;
}) {
  if (!email) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-amber-700">
      <T context="draft PO sending: the PO email does not go to the vendor's PO address">
        This PO is not addressed to the vendor's PO email.
      </T>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onUse(email)}
      >
        <T context="draft PO sending: button to send the PO to the vendor's email">
          Send to <Var>{email}</Var>
        </T>
      </Button>
    </div>
  );
}

function TextField({
  id,
  value,
  type = 'text',
  onChange,
  children,
}: {
  id: string;
  value: string;
  type?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{children}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        maxLength={60}
        onChange={onChange}
      />
    </div>
  );
}

function CreditTermsField({
  id,
  value,
  codes,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  codes: DraftPurchaseOrderSubmission['creditTermsCodes'];
  disabled: boolean;
  onChange: (code: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>
        <T context="draft PO sending field">Credit terms</T>
      </Label>
      <Select
        value={value || NO_CREDIT_TERMS}
        disabled={disabled}
        onValueChange={(code) =>
          onChange(!code || code === NO_CREDIT_TERMS ? '' : code)
        }
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_CREDIT_TERMS}>
            <T context="credit terms picker: no credit terms on the PO">None</T>
          </SelectItem>
          {codes.map((entry) => (
            <SelectItem key={entry.code} value={entry.code}>
              {creditTermsLabel(entry.code, entry.description)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function InstructionRows({
  rows,
  disabled,
  onChange,
}: {
  rows: DraftSubmissionForm['instructions'];
  disabled: boolean;
  onChange: (rows: DraftSubmissionForm['instructions']) => void;
}) {
  const gt = useGT();
  const update = (index: number, patch: Partial<(typeof rows)[number]>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <fieldset className="space-y-1">
      <legend className="text-sm font-medium">
        <T context="draft PO sending field: lines printed on the PO">
          Special instructions
        </T>
      </legend>
      <p className="text-muted-foreground text-xs">
        <T context="draft PO sending field hint: what special instructions are for">
          The vendor's standing notes from the vendor master, and your email.
        </T>
      </p>
      {rows.map((row, index) => (
        <div key={row.slot} className="flex gap-2">
          <Input
            aria-label={gt('Special instruction line {line}', {
              context: 'draft PO sending: one printed line',
              line: String(index + 1),
            })}
            className="font-mono"
            value={row.text}
            maxLength={SPECIAL_INSTRUCTION_WIDTH}
            onChange={(event) => update(index, { text: event.target.value })}
          />
          <Select
            value={row.code}
            disabled={disabled}
            onValueChange={(code) => update(index, { code: code ?? NO_CODE })}
          >
            <SelectTrigger
              className="w-24"
              aria-label={gt('Usage code', {
                context:
                  "draft PO sending: the vendor master's code on a printed line (P or B)",
              })}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_CODE}>
                <T context="usage code picker: no code">None</T>
              </SelectItem>
              {USAGE_CODES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </fieldset>
  );
}
